import { UserState } from '../reducers/userSlice';
import { TaskRouterLocalStorageInfo } from '../redux/features/taskRouter/taskRouter.props';
import logger from './logger';
import { nullOrUndefined } from './types';

const USER_STATE_KEY = 'user-info';
const USER_STATE_VERSION_KEY = 'user-info-version';
const AUTH_TOKEN_KEY = 'auth-token';
const TASKROUTER_AGENT_INFO = 'task-router-agent-info';
const LAYOUT_PREFERENCE_KEY = 'hitl-layout-preference';

export const saveAuthToken = (authToken: string | nullOrUndefined) => {
  if (authToken) {
    localStorage.setItem(AUTH_TOKEN_KEY, authToken);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
};

export const getAuthToken = (): string | undefined => {
  const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
  if (authToken && authToken !== '') {
    return authToken;
  }
  return undefined;
};

// Change this versionNumber whenever the format for user state changes so we know to delete the old data
const userStateVersion = '1.0.0';
export const getUserState = (): UserState | undefined => {
  // If called on server, localStorage doesn't exist
  if (typeof window === 'undefined') {
    return undefined;
  }
  const serializedState = localStorage.getItem(USER_STATE_KEY);
  if (serializedState === null) {
    return undefined;
  }
  const foundUserStateVersion = localStorage.getItem(USER_STATE_VERSION_KEY);
  if (!foundUserStateVersion || foundUserStateVersion !== userStateVersion) {
    logger.warn('User state version is out of date. Disregarding');
    localStorage.removeItem(USER_STATE_KEY);
    return undefined;
  }
  const userState = JSON.parse(serializedState);
  if (userState) {
    return userState;
  }
  return undefined;
};

export const saveUserState = (userState: UserState) => {
  const serializedState = JSON.stringify(userState);
  localStorage.setItem(USER_STATE_KEY, serializedState);
  localStorage.setItem(USER_STATE_VERSION_KEY, userStateVersion);
};

export const saveTaskRouterInfoInLS = (
  taskRouterInfo: TaskRouterLocalStorageInfo
) => {
  const serializedState = JSON.stringify(taskRouterInfo);
  localStorage.setItem(TASKROUTER_AGENT_INFO, serializedState);
};

export const getTaskRouterInfoFromLS = () => {
  const serializedState: any =
    localStorage.getItem(TASKROUTER_AGENT_INFO) || null;
  return JSON.parse(serializedState);
};

export const updateTaskRouterInfoInLS = (obj: any) => {
  const oldValue = getTaskRouterInfoFromLS();
  const serializedState = JSON.stringify({ ...oldValue, ...obj });
  localStorage.setItem(TASKROUTER_AGENT_INFO, serializedState);
};

export const removeTaskRouterInfoFromLS = () => {
  localStorage.removeItem(TASKROUTER_AGENT_INFO);
};

export const getLoggedInUserEmail = () => {
  const userInfo = localStorage.getItem(USER_STATE_KEY) || '';
  return userInfo ? JSON.parse(userInfo).userProfile?.email : '';
};

export const getTaskRouterAgentId = () => {
  const agentInfo = localStorage.getItem(TASKROUTER_AGENT_INFO) || '';
  return agentInfo ? JSON.parse(agentInfo).agentId : '';
};

/**
 * Resolves the per-agent suffix used to scope both the resizable-panes storage
 * (`hitl-console-panes:<key>`) and the explicit layout preference below to the
 * signed-in user. Kept in one place so the two stay in lock-step.
 */
export const resolvePaneUserKey = (
  userProfile?: { username?: string; email?: string } | null
) => userProfile?.username || userProfile?.email || 'default';

/**
 * The agent's explicit pane split, as [left, middle, right] percentages summing
 * to 100. Written ONLY when the agent hits "Apply" in the Layout preference
 * dialog — react-resizable-panels auto-persists its own sizes on every layout
 * (including the default on first mount), so that store can't tell an explicit
 * choice from an auto-saved default. This dedicated key is that signal: when it
 * exists, the saved split is honoured on task arrival instead of the transcript
 * auto-expanding.
 */
export const getLayoutPreference = (
  userKey: string
): [number, number, number] | null => {
  try {
    const raw = localStorage.getItem(`${LAYOUT_PREFERENCE_KEY}:${userKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.length === 3 &&
      parsed.every(
        (size) => typeof size === 'number' && Number.isFinite(size)
      ) &&
      Math.round(parsed.reduce((sum, size) => sum + size, 0)) === 100
    ) {
      return parsed as [number, number, number];
    }
  } catch (error) {
    logger.warn('Could not read layout preference from local storage');
  }
  return null;
};

export const saveLayoutPreference = (
  userKey: string,
  sizes: [number, number, number]
) => {
  localStorage.setItem(
    `${LAYOUT_PREFERENCE_KEY}:${userKey}`,
    JSON.stringify(sizes)
  );
};

export const removeLayoutPreference = (userKey: string) => {
  localStorage.removeItem(`${LAYOUT_PREFERENCE_KEY}:${userKey}`);
};
